import React, { useState } from 'react'
import { Input } from '~/lib/ui/input'
import { Label } from '~/lib/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/lib/ui/select'
import { StateSelect } from '~/lib/ui/StateSelect'
import { CountrySelect } from '~/lib/ui/CountrySelect'
import { Button } from '~/lib/ui/button'
import { apiPatch } from '~/util/api'
import { useMutation } from '@tanstack/react-query'
import { useToast } from '~/lib/ui/toast'
import { useCandidate } from './CandidateProvider'

const EditCandidateForm = ({ onClose }) => {
  const { toast } = useToast()
  const { candidate, setCandidate } = useCandidate()
  const [phone, setPhone] = useState(candidate.phone || '')
  const [phoneType, setPhoneType] = useState(
    candidate.phoneType?.toLowerCase() || 'landline',
  )
  const [address, setAddress] = useState(candidate.streetAddress || '')
  const [city, setCity] = useState(candidate.city || '')
  const [state, setState] = useState(candidate.state || '')
  const [postalCode, setPostalCode] = useState(candidate.postalCode || '')
  const [country, setCountry] = useState(candidate.country || 'US')
  const [formError, setFormError] = useState('')

  // Career dev
  const profile = candidate.candidate_profile || {}
  const [jobRole, setJobRole] = useState(profile.job_role || 'Employee')
  const [managerFirstName, setManagerFirstName] = useState(
    profile.manager_first_name || '',
  )
  const [managerLastName, setManagerLastName] = useState(
    profile.manager_last_name || '',
  )
  const [managerEmail, setManagerEmail] = useState(profile.manager_email || '')
  const [managerPhone, setManagerPhone] = useState(profile.manager_phone || '')

  const handlePhoneTypeChange = (value) => {
    setPhoneType(value)
    if (candidate.mobile_phone && (candidate.us || candidate.canadian)) {
      if (value === 'mobile') {
        setPhone((prev) => (prev.length <= 12 ? prev : ''))
      }
    }
  }

  const mutation = useMutation({
    mutationFn: async (formData) =>
      apiPatch(`/coach/candidates/${candidate.id}`, formData),
    onSuccess: (data) => {
      toast({ title: 'Success', description: 'Candidate updated successfully' })
      // Update candidate state in context
      if (data && data.data.candidate) {
        setCandidate(data.data.candidate)
      }
      onClose()
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Unable to update candidate',
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = () => {
    // Reset previous error
    setFormError('')

    // Validate mobile phone format
    const isMobile = phoneType === 'mobile'
    const mobileFormat = /^\d{3}-\d{3}-\d{4}$/
    if (isMobile && !mobileFormat.test(phone)) {
      setFormError('Mobile Phone must be in the following format: 555-555-5555')
      return
    }

    const formData = new FormData()
    formData.append('candidate[phone]', phone)
    formData.append('candidate[phone_type]', phoneType)
    if (!candidate.pre_platform) {
      formData.append('candidate[address]', address)
      formData.append('candidate[city]', city)
      formData.append('candidate[state]', state)
      formData.append('candidate[postal_code]', postalCode)
      formData.append('candidate[country]', country)
    }
    if (candidate.career_development) {
      formData.append(
        'candidate[candidate_profile_attributes][job_role]',
        jobRole,
      )
      formData.append(
        'candidate[candidate_profile_attributes][manager_first_name]',
        managerFirstName,
      )
      formData.append(
        'candidate[candidate_profile_attributes][manager_last_name]',
        managerLastName,
      )
      formData.append(
        'candidate[candidate_profile_attributes][manager_email]',
        managerEmail,
      )
      formData.append(
        'candidate[candidate_profile_attributes][manager_phone]',
        managerPhone,
      )
    }

    mutation.mutate(formData)
  }

  return (
    <form className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          {formError && (
            <div className="rounded-md border border-red-500 bg-red-100 px-4 py-2 text-sm text-red-800">
              {formError}
            </div>
          )}
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={phone}
            placeholder={
              candidate.mobile_phone && (candidate.us || candidate.canadian)
                ? '555-555-5555'
                : ''
            }
            maxLength={
              candidate.mobile_phone && (candidate.us || candidate.canadian)
                ? 12
                : undefined
            }
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div className="pt-7">
          <label className="mr-4">
            <input
              type="radio"
              value="landline"
              checked={!phoneType || phoneType.toLowerCase() === 'landline'}
              onChange={() => handlePhoneTypeChange('landline')}
              className="!mt-[-5px]"
            />
            &nbsp;Landline
          </label>
          <label>
            <input
              type="radio"
              value="mobile"
              checked={phoneType.toLowerCase() === 'mobile'}
              onChange={() => handlePhoneTypeChange('mobile')}
              className="!mt-[-5px]"
            />
            &nbsp;Mobile
          </label>
        </div>
      </div>

      {!candidate.pre_platform && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Address</Label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div>
              <Label>City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label>State</Label>
              <StateSelect value={state} onValueChange={setState} />
            </div>
            <div>
              <Label>Postal Code</Label>
              <Input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
              />
            </div>
            <div>
              <Label>Country</Label>
              <CountrySelect value={country} onValueChange={setCountry} />
            </div>
          </div>
        </>
      )}

      {candidate.career_development && (
        <>
          <div>
            <Label>Employee Level</Label>
            <Select onValueChange={setJobRole} defaultValue={jobRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Employee">Employee</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label>First Name</Label>
              <Input
                value={managerFirstName}
                onChange={(e) => setManagerFirstName(e.target.value)}
              />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input
                value={managerLastName}
                onChange={(e) => setManagerLastName(e.target.value)}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={managerEmail}
                onChange={(e) => setManagerEmail(e.target.value)}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={managerPhone}
                onChange={(e) => setManagerPhone(e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-4">
        <Button type="button" onClick={handleSubmit}>
          Save
        </Button>
        <Button
          type="button"
          variant="outline"
          className="ml-2"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default EditCandidateForm
